import PropTypes from 'prop-types'
import React, { useMemo, useRef } from 'react'
import { getFontSize } from '../../../helpers/fontSize'
import { UseTypeFontSize } from '../../hooks/useTypeFontSize'
import { LABEL_OPTIONS_DEFAULT, RADIAN } from './constants'
import { getLabelSize } from './helper'

export const ChartLabel = ({
  data,
  width,
  height,
  minValueHandle,
  rangeBetweenLabelLine,
  rangeBetweenPieAndLine,
  firstLabelLineLength,
  secondLabelLineLength,
  yTextFromBottom,
  renderCustomLabel,
  labelLineColor,
  ...props
}) => {
  const { cx, cy, midAngle, outerRadius, percent, index, payload } = props
  const typeFontSize = UseTypeFontSize()
  const fontSize = getFontSize(12, typeFontSize)

  // Get data
  const labelSize = useMemo(() => {
    return getLabelSize(payload, percent, index, fontSize, renderCustomLabel)
  }, [renderCustomLabel])

  const getLabelWidth = useMemo(() => {
    if (renderCustomLabel) {
      return labelSize.width
    } else {
      return labelSize.width + fontSize / 2
    }
  }, [labelSize])

  const getLabelHeight = useMemo(() => {
    return labelSize.height
  }, [labelSize])

  const sumValue = data.reduce((sum, item) => {
    return sum + item.value
  }, 0)

  const dataSmall =
    data.filter((item) => item.value <= (minValueHandle * sumValue) / 100) || []

  const lastExtraMy = useRef((dataSmall.length / 2) * -rangeBetweenLabelLine)

  if (index === 0) {
    lastExtraMy.current = (dataSmall.length / 2) * -rangeBetweenLabelLine
  }

  const isHandled = percent * 100 <= minValueHandle && dataSmall.length > 1

  // Svg parameters
  const sin = Math.sin(-RADIAN * midAngle)
  const cos = Math.cos(-RADIAN * midAngle)
  const sx = cx + (outerRadius + rangeBetweenPieAndLine) * cos
  const sy = cy + (outerRadius + rangeBetweenPieAndLine) * sin
  const xTextPlus = (cos >= 0 ? -1 : !renderCustomLabel ? 1 : 0) * getLabelWidth
  const yTextPlus = !renderCustomLabel ? yTextFromBottom : getLabelHeight
  const textAnchor = cos >= 0 ? 'start' : 'end'
  let mx = cx + (outerRadius + firstLabelLineLength) * cos
  let my = cy + (outerRadius + firstLabelLineLength) * sin
  let ex =
    mx + (cos >= 0 ? 1 : -1) * Math.max(secondLabelLineLength, getLabelWidth)
  let ey = my
  let zx = null
  let zy = null
  let xText = ex + xTextPlus
  let yText = ey - yTextPlus

  if (isHandled) {
    my = sy
    mx = 335
    ey += lastExtraMy.current
    lastExtraMy.current += rangeBetweenLabelLine
    ex = mx + 20
    zy = ey
    zx = ex + 42
    xText = zx - 34
    yText = zy - 4
  }

  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}${
          zx && zy ? ',' + zx + ',' + zy : ''
        }`}
        stroke={labelLineColor || payload.color}
        fill="none"
      />
      <circle
        cx={ex}
        cy={ey}
        r={2}
        fill={labelLineColor || payload.color}
        stroke="none"
      />
      {renderCustomLabel ? (
        <foreignObject
          x={xText}
          y={yText}
          width={getLabelWidth}
          height={getLabelHeight}
        >
          {renderCustomLabel(payload, percent, index)}
        </foreignObject>
      ) : (
        <text
          x={xText}
          y={yText}
          textAnchor={textAnchor}
          fontSize={fontSize}
          fontWeight={600}
          fill={payload.color}
        >{`${(percent * 100).toFixed(2)}%`}</text>
      )}
    </g>
  )
}

ChartLabel.propTypes = {
  data: PropTypes.array.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  minValueHandle: PropTypes.number,
  rangeBetweenLabelLine: PropTypes.number,
  rangeBetweenPieAndLine: PropTypes.number,
  firstLabelLineLength: PropTypes.number,
  secondLabelLineLength: PropTypes.number,
  yTextFromBottom: PropTypes.number,
  renderCustomLabel: PropTypes.func,
}

ChartLabel.defaultProps = LABEL_OPTIONS_DEFAULT
